import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { NetworkLink } from '#components/patterns/entity-links/network-link/NetworkLink.tsx';
import { RoleLink } from '#components/patterns/entity-links/role-link/RoleLink.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { JoinKeyListItem } from '#dn-types/join-key.ts';
import { ExpiresAt } from '#screens/settings/api-keys/detail/ExpiresAt.tsx';
import { JoinKeysActionMenu } from './JoinKeysActionMenu.tsx';
import styles from './JoinKeysList.module.css';

type Props = {
  data: JoinKeyListItem[];
  isFetching: boolean;
  className?: string;
};

const columnHelper = createColumnHelper<JoinKeyListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    size: 200,
    cell: (info) => (
      <Link className="a inline truncate" to={`/settings/join-keys/${info.row.original.id}`}>
        {info.getValue()}
      </Link>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('id', {
    header: 'ID',
    size: 160,
    cell: (info) => <CopyPill className="-ml-6" value={info.getValue()} />,
    meta: { className: styles.JoinKeysList_id },
  }),
  columnHelper.accessor('networkID', {
    header: 'Network',
    size: 140,
    cell: (info) => <NetworkLink networkId={info.getValue()} className="inline truncate" />,
    meta: { truncate: true, className: styles.JoinKeysList_network },
  }),
  columnHelper.accessor('roleID', {
    header: 'Role',
    size: 140,
    cell(info) {
      const roleID = info.getValue();
      if (!roleID) {
        return <span className="p-sm">None</span>;
      }
      return <RoleLink roleID={roleID} className="inline truncate" />;
    },
    meta: { truncate: true, className: styles.JoinKeysList_role },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    size: 240,
    cell: (info) => <Timestamp datetime={info.getValue()} />,
    meta: { truncate: true, className: styles.JoinKeysList_createdAt },
  }),
  columnHelper.accessor('expiresAt', {
    header: 'Expires',
    size: 140,
    cell: (info) => <ExpiresAt expiresAt={info.getValue()} />,
    meta: { truncate: true, className: styles.JoinKeysList_expiresAt },
  }),
  columnHelper.display({
    id: 'action-menu',
    header: '',
    size: 34,
    cell: (info) => (
      <div className="flex justify-end">
        <JoinKeysActionMenu value={info.row.original.id} />
      </div>
    ),
  }),
];

export function JoinKeysList({ data, isFetching, className }: Props) {
  return <Table className={cn({ 'opacity-dim': isFetching }, className)} data={data} columns={columns} />;
}
